import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../../components/redux/hooks/useOnboarding';
import { usePostHog } from '../../../components/analytics/PostHogProvider';


const PhoneSetup = () => {
  const navigate = useNavigate();
  const { captureEvent } = usePostHog();
  const { sessionId } = useOnboarding();
  
  const [selectedOption, setSelectedOption] = useState(null); // 'ankr' or 'existing'

  const handleUseAnkrNumber = () => {
    setSelectedOption('ankr');
    captureEvent('phone_setup_ankr_selected', { sessionId });
  };

  const handleUseExistingNumber = () => {
    setSelectedOption('existing');
    captureEvent('phone_setup_existing_selected', { sessionId });
  };

  const handlePrevious = () => {
    navigate('/onboarding/ehr-connect');
  };

  const handleSkip = () => {
    captureEvent('phone_setup_skipped', { sessionId });
    navigate('/onboarding/dashboard');
  };

  const handleContinue = () => {
    if (selectedOption === 'ankr') {
      captureEvent('phone_setup_ankr_confirmed', { sessionId });
      // TODO: Provision Ankr number
      navigate('/onboarding/dashboard');
    } else if (selectedOption === 'existing') {
      captureEvent('phone_setup_existing_confirmed', { sessionId });
      // TODO: Start porting/forwarding flow
      navigate('/onboarding/dashboard');
    }
  };

  const phoneTreeItems = [
    { number: '1', title: 'Appointments & Scheduling', description: 'Book, reschedule, or cancel appointments' },
    { number: '2', title: 'Prescription Refills', description: 'Request medication refills' },
    { number: '3', title: 'Medical Questions', description: 'Speak with a nurse or provider' },
    { number: '4', title: 'Billing & Insurance', description: 'Payment and coverage inquiries' },
    { number: '0', title: 'Emergency & Urgent Care', description: 'Immediate assistance available' },
  ];

  return (
    <div className="phone-setup-container">
      {/* Header */}
      <div className="phone-setup-header">
        <div className="header-left">
          <h1>Phone Setup</h1>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: '75%' }}></div>
          </div>
        </div>
        <div className="header-right">
          <span className="xp-badge">⭐ 200 XP upon completion</span>
          <span className="step-indicator">Step 6 of 8</span>
        </div>
      </div>

      {/* Quick Start Banner */}
      <div className="quick-start-banner">
        <div className="banner-icon">⚡</div>
        <div className="banner-content">
          <h3>Quick Start Available</h3>
          <p>If you provision today, <span className="highlight">Mira can start handling IVR test calls within 4 hours</span></p>
        </div>
      </div>

      {/* Phone Setup Options */}
      <div className="setup-options-section">
        <h2>Choose Your Phone Setup Method</h2>
        <p className="section-subtitle">Select how you want to configure your phone system for Ankr&apos;s AI assistant</p>

        <div className="options-container">
          {/* Option 1: Use Ankr Number */}
          <div className={`option-card ${selectedOption === 'ankr' ? 'selected' : ''}`} onClick={handleUseAnkrNumber}>
            <div className="option-header">
              <div className="option-icon ankr-icon">
                <span>📞</span>
              </div>
              <div className="option-title">
                <h3>Use Ankr Number</h3>
                <p>Get a new phone number provisioned by Ankr</p>
              </div>
            </div>

            <div className="option-features">
              <div className="feature-item">
                <span className="feature-check">✓</span>
                <div>
                  <strong>Quick Setup</strong>
                  <p>No carrier changes required</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-check">✓</span>
                <div>
                  <strong>Local Number</strong>
                  <p>Choose from available local area codes</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-check">✓</span>
                <div>
                  <strong>Call Forwarding</strong>
                  <p>Forward to your existing clinic line</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-check">✓</span>
                <div>
                  <strong>Voicemail & SMS</strong>
                  <p>Full messaging capabilities included</p>
                </div>
              </div>
            </div>

            <div className="sample-number">
              <div className="sample-info">
                <span className="sample-label">Sample Number</span>
                <span className="sample-location">📍 San Francisco, CA • Available now</span>
              </div>
              <span className="sample-phone">(555) 234-5678</span>
            </div>

            <button className="option-button primary" onClick={(e) => { e.stopPropagation(); handleContinue(); }} disabled={selectedOption !== 'ankr'}>
              → Use Ankr Number
            </button>
          </div>

          {/* Option 2: Use Existing Number */}
          <div className={`option-card ${selectedOption === 'existing' ? 'selected' : ''}`} onClick={handleUseExistingNumber}>
            <div className="option-header">
              <div className="option-icon existing-icon">
                <span>📱</span>
              </div>
              <div className="option-title">
                <h3>Use Existing Number</h3>
                <p>Integrate with your current clinic number</p>
              </div>
            </div>

            <div className="option-features">
              <div className="feature-item">
                <span className="feature-star">⭐</span>
                <div>
                  <strong>Call Forwarding</strong>
                  <p>Forward calls to Ankr during business hours</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-star">⭐</span>
                <div>
                  <strong>Port Number</strong>
                  <p>Transfer your number to Ankr completely</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-star">⭐</span>
                <div>
                  <strong>IVR Integration</strong>
                  <p>Connect to your existing phone tree</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-star">⭐</span>
                <div>
                  <strong>Carrier Coordination</strong>
                  <p>We handle the technical setup with your provider</p>
                </div>
              </div>
            </div>

            <div className="setup-process">
              <div className="process-header">
                <span className="process-icon">ℹ️</span>
                <span>Setup Process</span>
              </div>
              <ol className="process-steps">
                <li>Verify ownership of your number</li>
                <li>Choose forwarding or porting</li>
                <li>Configure call routing rules</li>
                <li>Test integration with Mira</li>
              </ol>
            </div>

            <button className="option-button secondary" onClick={(e) => { e.stopPropagation(); handleContinue(); }} disabled={selectedOption !== 'existing'}>
              → Use Existing Number
            </button>
          </div>
        </div>
      </div>

      {/* Phone Tree Configuration Preview */}
      <div className="phone-tree-section">
        <div className="phone-tree-card">
          <h2>Sample Phone Tree Configuration</h2>
          <p className="section-subtitle">Preview how Mira will handle your calls</p>

          <div className="phone-tree-container">
          <div className="incoming-call">
            <div className="call-icon">📞</div>
            <div className="call-info">
              <strong>Incoming Call</strong>
              <p>Mira answers within 2 rings</p>
            </div>
          </div>

          <div className="tree-items">
            {phoneTreeItems.map((item) => (
              <div key={item.number} className={`tree-item ${item.number === '0' ? 'emergency' : ''}`}>
                <div className={`item-number ${item.number === '0' ? 'emergency' : ''}`}>{item.number}</div>
                <div className="item-content">
                  <strong>{item.title}</strong>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>

      {/* Benefits Footer */}
      <div className="benefits-footer">
        <div className="benefit-item">
          <span className="benefit-icon">⏱️</span>
          <div>
            <strong>4-Hour Activation</strong>
            <p>Go live today with instant provisioning</p>
          </div>
        </div>
        <div className="benefit-item">
          <span className="benefit-icon">🛡️</span>
          <div>
            <strong>HIPAA Compliant</strong>
            <p>Secure handling of patient information</p>
          </div>
        </div>
        <div className="benefit-item">
          <span className="benefit-icon">📊</span>
          <div>
            <strong>Call Analytics</strong>
            <p>Real-time insights and reporting</p>
          </div>
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="navigation-footer">
        <button className="nav-button previous" onClick={handlePrevious}>
          ← Previous
        </button>
        <button className="nav-button skip" onClick={handleSkip}>
          Skip for Now →→
        </button>
      </div>
    </div>
  );
};

export default PhoneSetup;
